/**
 * Dashboard Page
 * P2 REFACTORED: Split into smaller components for better maintainability
 * Components: DashboardStats, DashboardCharts, PayRulesTable
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "@/lib/api";
import { useCompanyFilter } from "@/contexts/CompanyFilterContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

// Extracted components
import { DashboardStats, DashboardCharts, PayRulesTable } from "@/components/dashboard";

const Dashboard = () => {
  const { selectedCompany, getApiCompanyId } = useCompanyFilter();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedState, setSelectedState] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  // Global severity filter - All severities checked by default
  const [severityFilters, setSeverityFilters] = useState({
    Critical: true,
    High: true,
    Medium: true,
    Low: true,
    Info: true,
    None: true
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "ComplianceStatus", direction: "asc" });
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [summary, setSummary] = useState({
    total: 0,
    compliant: 0,
    non_compliant: 0,
    over_compliant: 0,
    critical_count: 0,
    rules: []
  });
  // Category data from new vewPayRuleCategoryTypeSummary view
  const [categoryDataFromView, setCategoryDataFromView] = useState([]);

  // Fetch main summary data (rules list)
  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const companyId = getApiCompanyId();
      const params = companyId ? { company_id: companyId } : {};
      
      const summaryRes = await apiClient.get(`/api/pay-rules/summary`, { params });
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getApiCompanyId]);

  // Fetch category chart data with severity filters
  const fetchCategoryData = useCallback(async () => {
    try {
      const companyId = getApiCompanyId();
      const params = { };
      
      if (companyId) {
        params.company_id = companyId;
      }
      
      // Build severities param from filter state
      const activeSeverities = Object.entries(severityFilters)
        .filter(([_, enabled]) => enabled)
        .map(([severity, _]) => severity);
      
      if (activeSeverities.length > 0) {
        params.severities = activeSeverities.join(',');
      }
      
      const categoryRes = await apiClient.get(`/api/category-summary`, { params });
      setCategoryDataFromView(categoryRes.data || []);
    } catch (error) {
      console.error("Failed to load category data:", error);
    }
  }, [getApiCompanyId, severityFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedCompany]);

  // Refetch category data when severity filters or company changes
  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData, selectedCompany]);

  const uniqueStates = useMemo(() => {
    const states = new Set(summary.rules.map(r => r.State).filter(Boolean));
    return Array.from(states).sort();
  }, [summary.rules]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const filteredAndSortedRules = useMemo(() => {
    let rules = [...summary.rules];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      rules = rules.filter(rule =>
        rule.PayRuleName?.toLowerCase().includes(search) ||
        rule.CompanyName?.toLowerCase().includes(search) ||
        rule.State?.toLowerCase().includes(search) ||
        rule.Title?.toLowerCase().includes(search) ||
        rule.Category?.toLowerCase().includes(search)
      );
    }

    if (selectedState !== "all") {
      rules = rules.filter(rule => rule.State === selectedState);
    }

    if (selectedStatus !== "all") {
      rules = rules.filter(rule => rule.ComplianceStatus === selectedStatus);
    }

    // Apply severity checkbox filters based on issue counts and compliance status
    // Critical = CriticalCount > 0
    // High = Non-Compliant with WarningCount > 0 but no Critical
    // Medium/Low = Non-Compliant with lower severity (included with High for now)
    // Info = Over-Compliant rules (their issues are Info severity)
    // None = Compliant rules with no issues
    const anySeveritySelected = Object.values(severityFilters).some(v => v);
    if (anySeveritySelected) {
      rules = rules.filter(rule => {
        const hasCritical = (rule.CriticalCount || 0) > 0;
        const hasWarning = (rule.WarningCount || 0) > 0;
        const isNonCompliant = rule.ComplianceStatus === 'Non-Compliant';
        const isOverCompliant = rule.ComplianceStatus === 'Over-Compliant';
        const isCompliant = rule.ComplianceStatus === 'Compliant';
        const hasNoIssues = !hasCritical && !hasWarning;
        
        // Critical: rules with critical issues
        if (severityFilters.Critical && hasCritical) return true;
        
        // High/Medium/Low: Non-Compliant rules with non-critical issues
        if ((severityFilters.High || severityFilters.Medium || severityFilters.Low) && 
            isNonCompliant && hasWarning && !hasCritical) return true;
        
        // Info: Over-Compliant rules (their issues are Info level)
        if (severityFilters.Info && isOverCompliant) return true;
        
        // None: Compliant rules with no issues
        if (severityFilters.None && (isCompliant || hasNoIssues)) return true;
        
        return false;
      });
    }

    const statusOrder = { "Non-Compliant": 1, "Over-Compliant": 2, "Compliant": 3 };
    const severityOrder = (rule) => {
      if ((rule.CriticalCount || 0) > 0) return 1;
      if ((rule.WarningCount || 0) > 0) return 2;
      return 3;
    };
    
    rules.sort((a, b) => {
      let primaryCompare = 0;
      
      switch (sortConfig.key) {
        case "PayRuleName":
          primaryCompare = sortConfig.direction === "asc" 
            ? (a.PayRuleName || "").localeCompare(b.PayRuleName || "")
            : (b.PayRuleName || "").localeCompare(a.PayRuleName || "");
          break;
        case "CompanyName":
          primaryCompare = sortConfig.direction === "asc" 
            ? (a.CompanyName || "").localeCompare(b.CompanyName || "")
            : (b.CompanyName || "").localeCompare(a.CompanyName || "");
          break;
        case "State":
          primaryCompare = sortConfig.direction === "asc" 
            ? (a.State || "").localeCompare(b.State || "")
            : (b.State || "").localeCompare(a.State || "");
          break;
        case "EmployeeCount":
          primaryCompare = sortConfig.direction === "asc" 
            ? (a.EmployeeCount || 0) - (b.EmployeeCount || 0)
            : (b.EmployeeCount || 0) - (a.EmployeeCount || 0);
          break;
        case "ComplianceStatus":
          const aVal = statusOrder[a.ComplianceStatus] || 4;
          const bVal = statusOrder[b.ComplianceStatus] || 4;
          primaryCompare = sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
          break;
        case "Severity":
          const aSev = severityOrder(a);
          const bSev = severityOrder(b);
          primaryCompare = sortConfig.direction === "asc" ? aSev - bSev : bSev - aSev;
          break;
        case "IssueCount":
          const aIssues = (a.CriticalCount || 0) + (a.WarningCount || 0);
          const bIssues = (b.CriticalCount || 0) + (b.WarningCount || 0);
          primaryCompare = sortConfig.direction === "asc" ? aIssues - bIssues : bIssues - aIssues;
          break;
        default:
          break;
      }

      if (primaryCompare === 0) {
        if (sortConfig.key === "ComplianceStatus") {
          const aSev = severityOrder(a);
          const bSev = severityOrder(b);
          if (aSev !== bSev) return aSev - bSev;
        }
        if (sortConfig.key === "Severity") {
          const aStatusVal = statusOrder[a.ComplianceStatus] || 4;
          const bStatusVal = statusOrder[b.ComplianceStatus] || 4;
          if (aStatusVal !== bStatusVal) return aStatusVal - bStatusVal;
        }
      }

      if (primaryCompare === 0) {
        return (b.EmployeeCount || 0) - (a.EmployeeCount || 0);
      }
      return primaryCompare;
    });

    return rules;
  }, [summary.rules, searchTerm, selectedState, selectedStatus, severityFilters, sortConfig]);

  // Group rules by PayRuleName + CompanyName for accordion view
  const groupedRules = useMemo(() => {
    const groups = {};
    
    filteredAndSortedRules.forEach(rule => {
      const key = `${rule.PayRuleName}|||${rule.CompanyName}`;
      if (!groups[key]) {
        groups[key] = {
          groupKey: key,
          PayRuleName: rule.PayRuleName,
          CompanyName: rule.CompanyName,
          states: [],
          totalEmployees: 0,
          totalCritical: 0,
          totalWarning: 0,
          statuses: new Set(),
          firstPayRuleID: rule.PayRuleID
        };
      }
      groups[key].states.push({
        PayRuleID: rule.PayRuleID,
        State: rule.State,
        EmployeeCount: rule.EmployeeCount || 0,
        ComplianceStatus: rule.ComplianceStatus,
        CriticalCount: rule.CriticalCount || 0,
        WarningCount: rule.WarningCount || 0
      });
      groups[key].totalEmployees += rule.EmployeeCount || 0;
      groups[key].totalCritical += rule.CriticalCount || 0;
      groups[key].totalWarning += rule.WarningCount || 0;
      groups[key].statuses.add(rule.ComplianceStatus);
    });

    return Object.values(groups).map(group => {
      let overallStatus;
      if (group.statuses.size > 1) {
        overallStatus = "Mixed";
      } else {
        overallStatus = Array.from(group.statuses)[0] || "Unknown";
      }
      let severity = "None";
      if (group.totalCritical > 0) {
        severity = "Critical";
      } else if (group.totalWarning > 0) {
        severity = "High";
      }
      return { ...group, overallStatus, severity, stateCount: group.states.length };
    });
  }, [filteredAndSortedRules]);

  const toggleExpanded = (groupKey) => {
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Filtered summary for stats tiles and charts
  const filteredSummary = useMemo(() => {
    const compliant = filteredAndSortedRules.filter(r => r.ComplianceStatus === "Compliant").length;
    const nonCompliant = filteredAndSortedRules.filter(r => r.ComplianceStatus === "Non-Compliant").length;
    const overCompliant = filteredAndSortedRules.filter(r => r.ComplianceStatus === "Over-Compliant").length;
    // Count rules that have critical issues (not total critical issue count)
    const criticalRuleCount = filteredAndSortedRules.filter(r => (r.CriticalCount || 0) > 0).length;
    
    return {
      total: filteredAndSortedRules.length,
      compliant,
      non_compliant: nonCompliant,
      over_compliant: overCompliant,
      critical_count: criticalRuleCount
    };
  }, [filteredAndSortedRules]);

  // Chart data - use filtered rules so charts update with filters
  const employeeByStatus = useMemo(() => {
    const data = { Compliant: 0, "Non-Compliant": 0, "Over-Compliant": 0 };
    filteredAndSortedRules.forEach(rule => {
      const emp = rule.EmployeeCount || 0;
      if (data[rule.ComplianceStatus] !== undefined) {
        data[rule.ComplianceStatus] += emp;
      }
    });
    const total = data.Compliant + data["Non-Compliant"] + data["Over-Compliant"];
    return [
      { name: "Compliant", value: data.Compliant, color: "#10B981", percent: total > 0 ? ((data.Compliant / total) * 100).toFixed(0) : 0 },
      { name: "Non-Compliant", value: data["Non-Compliant"], color: "#EF4444", percent: total > 0 ? ((data["Non-Compliant"] / total) * 100).toFixed(0) : 0 },
      { name: "Over-Compliant", value: data["Over-Compliant"], color: "#F59E0B", percent: total > 0 ? ((data["Over-Compliant"] / total) * 100).toFixed(0) : 0 }
    ].filter(d => d.value > 0);
  }, [filteredAndSortedRules]);

  const employeeStatusBars = useMemo(() => {
    const nonCompliant = employeeByStatus.find(e => e.name === "Non-Compliant");
    const overCompliant = employeeByStatus.find(e => e.name === "Over-Compliant");
    const compliant = employeeByStatus.find(e => e.name === "Compliant");
    return { nonCompliant, overCompliant, compliant };
  }, [employeeByStatus]);

  const categoryData = useMemo(() => {
    // Use data from vewPayRuleCategoryTypeSummary view (server-side aggregated)
    // Format for chart: top 5 categories by total, with truncated names (16 chars for wider Y-axis)
    return categoryDataFromView
      .slice(0, 5)
      .map(cat => ({
        name: cat.name.length > 16 ? cat.name.substring(0, 16) + ".." : cat.name,
        fullName: cat.name,
        Compliant: cat.Compliant || 0,
        "Non-Compliant": cat["Non-Compliant"] || 0,
        "Over-Compliant": cat["Over-Compliant"] || 0,
        total: cat.total || 0,
        totalErrors: cat.totalErrors || 0,
        totalEmployees: cat.totalEmployees || 0
      }));
  }, [categoryDataFromView]);

  const totalEmployees = useMemo(() => {
    return filteredAndSortedRules.reduce((sum, r) => sum + (r.EmployeeCount || 0), 0);
  }, [filteredAndSortedRules]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedState("all");
    setSelectedStatus("all");
    // Reset severity to default (all checked)
    setSeverityFilters({ Critical: true, High: true, Medium: true, Low: true, Info: true, None: true });
  };

  // Check if filters differ from default (all severities on)
  const hasActiveFilters = searchTerm || 
    selectedState !== "all" || 
    selectedStatus !== "all" || 
    !severityFilters.Critical || 
    !severityFilters.High || 
    !severityFilters.Medium ||
    !severityFilters.Low ||
    !severityFilters.Info ||
    !severityFilters.None;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-3.5rem)] flex flex-col animate-fade-in" data-testid="dashboard">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Compliance Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Tiles */}
      <DashboardStats summary={filteredSummary} />

      {/* Charts Row */}
      <DashboardCharts
        summary={filteredSummary}
        totalEmployees={totalEmployees}
        employeeByStatus={employeeByStatus}
        employeeStatusBars={employeeStatusBars}
        categoryData={categoryData}
      />

      {/* Pay Rules Table */}
      <PayRulesTable
        groupedRules={groupedRules}
        totalRules={filteredSummary.total}
        hasActiveFilters={hasActiveFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        severityFilters={severityFilters}
        setSeverityFilters={setSeverityFilters}
        uniqueStates={uniqueStates}
        clearFilters={clearFilters}
        sortConfig={sortConfig}
        handleSort={handleSort}
        expandedRules={expandedRules}
        toggleExpanded={toggleExpanded}
      />
    </div>
  );
};

export default Dashboard;
